import React, { useEffect } from 'react';
import { useSupport } from '../../redux/ducks/support';
import { useSelector } from 'react-redux';
import { isStringNotEmpty } from '../../utils/objectUtils';
import GuestRegistrationForm from './GuestRegistrationForm';
import SupportChatPanel from './SupportChatPanel';
import './SupportChat.css';

/**
 * SupportChatWidget - Floating chat button and panel
 *
 * Replaces Intercom widget. Renders a floating button (bottom-right)
 * that opens the support chat panel. For unauthenticated users,
 * shows the GuestRegistrationForm first.
 */
const SupportChatWidget = () => {

	//#region useSupport

	const {
		isWidgetOpen,
		toggleSupportWidget,
		hasActiveConversation,
		activeConversationId,
		isRegistering,
		startSupportChat,
		isLoading
	} = useSupport();

	const authUser = useSelector((appState) => appState.authState?.authUser);
	const isAuthenticated = isStringNotEmpty(authUser?.userId);
	//#endregion
	//#region autoStartChat

	// When an authenticated user opens the widget, auto-start their chat
	useEffect(() => {
		if (isWidgetOpen && isAuthenticated && !hasActiveConversation && !isLoading) {
			startSupportChat(authUser.userId, authUser.displayName || `${authUser.firstName} ${authUser.lastName}`, authUser.email);
		}
	}, [isWidgetOpen, isAuthenticated, hasActiveConversation, isLoading]);

	//#endregion
	//#region renderContent

	/**
	 * Determines which content to render inside the chat panel body:
	 * - GuestRegistrationForm for unauthenticated users without a conversation
	 * - SupportChatPanel once a conversation exists
	 * - Loading spinner while the conversation is being created
	 */
	const renderContent = () => {
		// Unauthenticated user needs to register first
		if (!isAuthenticated && !hasActiveConversation) {
			return <GuestRegistrationForm />;
		}

		// Show chat panel once we have a conversation
		if (hasActiveConversation) {
			return <SupportChatPanel conversationId={activeConversationId} />;
		}

		// Loading state while creating conversation
		return (
			<div className="support-chat-loading">
				<div className="spinner-border spinner-border-sm text-primary" role="status">
					<span className="visually-hidden">Loading...</span>
				</div>
				<p className="mt-2 mb-0 text-muted">Starting chat...</p>
			</div>
		);
	};

	//#endregion
	//#region returnJSX

	return (
		<>
			{/* Chat Panel */}
			{isWidgetOpen && (
				<div className="support-chat-container">
					<div className="support-chat-header">
						<div className="support-chat-header-info">
							<div className="support-chat-avatar">
								<i className="fas fa-headset"></i>
							</div>
							<div>
								<h6 className="mb-0">ReadyRx Support</h6>
								<small className="text-light opacity-75">We typically reply in a few minutes</small>
							</div>
						</div>
						<button
							className="support-chat-close-btn"
							onClick={toggleSupportWidget}
							aria-label="Close support chat"
						>
							<i className="fas fa-times"></i>
						</button>
					</div>
					<div className="support-chat-body">
						{renderContent()}
					</div>
				</div>
			)}

			{/* Floating Action Button - toggles the chat panel open/closed */}
			<button
				className={`support-chat-fab ${isWidgetOpen ? 'support-chat-fab-active' : ''}`}
				onClick={toggleSupportWidget}
				aria-label={isWidgetOpen ? 'Close support chat' : 'Open support chat'}
			>
				{isWidgetOpen ? (
					<i className="fas fa-chevron-down"></i>
				) : (
					<i className="fas fa-comment-dots"></i>
				)}
			</button>
		</>
	);
	//#endregion
};

export default SupportChatWidget;
